import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { AnnouncementListModel, ClientListModel, productModel } from "./types";
import queryClient from "../../../common/query-client";
import {
  useDelete,
  useGet,
  usePost,
  usePut,
} from "../../../common/api-service";
import useHomeStore from "./store";
import { AddAnnouncementSchema } from "./schema";
import { signFile } from "../../../common/utils/file-signing";
import { ProductListModel } from "../../products/api/types";
import useMainStore from "../../landing-page/api/store";

export const useGetClients = (pageNumber: number, pageSize: number) => {
  const { get } = useGet();
  var url = `/clients/list?PageNumber=${pageNumber}&PageSize=${pageSize}`;

  return useQuery<ClientListModel>({
    queryFn: () => get<ClientListModel>(url),
    queryKey: [url],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetProducts = (pageNumber: number, pageSize: number) => {
  const { get } = useGet();
  var url = `/api/products/list?PageNumber=${pageNumber}&PageSize=${pageSize}`;
  return useQuery<ProductListModel>({
    queryFn: () => get<ProductListModel>(url),
    queryKey: [url],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useCreateProduct = () => {
  const { post } = usePost();
  return useMutation({
    mutationFn: (data: productModel) => {
      return post(`/api/products/create`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/products/create`] });
    },
  });
};

export const useGetAnnouncements = () => {
  const { get } = useGet();
  const { initialAnnouncementState } = useHomeStore();
  let url = `/api/announcements/list?PageNumber=${initialAnnouncementState.pageNumber}&PageSize=${initialAnnouncementState.pageSize}`;

  if (initialAnnouncementState.searchText) {
    url += `&SearchText=${initialAnnouncementState.searchText}`;
  }

  return useQuery<AnnouncementListModel>({
    queryFn: () => get<AnnouncementListModel>(url),
    queryKey: [
      `announcement-list/${initialAnnouncementState.pageNumber}/${initialAnnouncementState.pageSize}/${initialAnnouncementState.searchText}`,
    ],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const convertAnnouncementFormSchemaToFormData = async (
  data: AddAnnouncementSchema,
): Promise<FormData> => {
  const formData = new FormData();

  formData.append("Title", data.title);
  if (data.body) formData.append("Body", data.body);
  
  if (data.id) formData.append("Id", data.id);
  if (data.date) formData.append("Date", data.date);
  if (data.category !== undefined)
    formData.append("Category", data.category.toString());

  if (
    data.announcementPhoto &&
    typeof data.announcementPhoto !== "string" &&
    data.announcementPhoto instanceof File
  ) {
    if (typeof data.announcementPhoto.arrayBuffer !== "function") {
      (data.announcementPhoto as any).arrayBuffer = async () =>
        new Response(data.announcementPhoto).arrayBuffer();
    }
    const signature = await signFile(data.announcementPhoto);
    formData.append("AnnouncementPhoto", data.announcementPhoto);
    formData.append("AnnouncementPhotoSignature", signature);
  } else {
  }

  return formData;
};

export const useCreateAnnouncement = () => {
  const { post } = usePost();
  const { initialAnnouncementState } = useHomeStore();
  return useMutation({
    mutationFn: async (data: AddAnnouncementSchema) => {
      const formData = await convertAnnouncementFormSchemaToFormData(data);
      return post(`/api/announcements/create`, formData, true, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          `announcement-list/${initialAnnouncementState.pageNumber}/${initialAnnouncementState.pageSize}/${initialAnnouncementState.searchText}`,
        ],
      });
    },
  });
};

export const useUpdateAnnouncement = () => {
  const { put } = usePut();
  const { initialAnnouncementState } = useHomeStore();

  return useMutation({
    mutationFn: async (data: AddAnnouncementSchema) => {
      const formData = await convertAnnouncementFormSchemaToFormData(data);
      return put(`/api/announcements/update`, formData, true, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          `announcement-list/${initialAnnouncementState.pageNumber}/${initialAnnouncementState.pageSize}/${initialAnnouncementState.searchText}`,
        ],
    });    },
  });
};


export const useDeleteAnnouncement = () => {
  const { deleteRequest } = useDelete();
  const { initialAnnouncementState } = useHomeStore();

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `/api/announcements/${id}`;
      return deleteRequest(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          `announcement-list/${initialAnnouncementState.pageNumber}/${initialAnnouncementState.pageSize}/${initialAnnouncementState.searchText}`,
        ],
      });
    },
  });
};

export const useGetConnectedApps = () => {
  const { get } = useGet();
  const { initialProductState } = useHomeStore();
  const { selectedClient, currentUser } = useMainStore();

  const { pageNumber, pageSize, searchText, onlyActive } = initialProductState;

  let url = `/api/products/list?PageNumber=${pageNumber}&PageSize=${pageSize}`;

  if (searchText) {
    url += `&SearchText=${encodeURIComponent(searchText)}`;
  }

  if (onlyActive) {
    url += `&OnlyActive=true`;
  }

  if (selectedClient?.id) {
    url += `&ClientId=${encodeURIComponent(selectedClient.id)}`;
  }

  return useQuery<ProductListModel, Error>({
    queryKey: [
      "connected-apps",
      pageNumber,
      pageSize,
      searchText ?? "",
      onlyActive,
      selectedClient?.id ?? "none",
    ],
    queryFn: () => get<ProductListModel>(url),
    enabled: !!currentUser,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};
